import { useCallback, useRef } from "react";

import { METADATA_TYPE } from "app/components/widget/widget-types/EngagedVisitsSupportGraphWidget";
import { minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import { useHDBannerCta } from "app/marketing/components/common/banners/HDBanner/useHDBannerCta";
import {
    PopTooltip,
    PopTooltipSingle,
} from "app/pages/website-analysis/TrafficAndEngagement/ChartTooltips/ChartTooltips";
import { CompareModeTooltip } from "app/pages/website-analysis/TrafficAndEngagement/ChartTooltips/CommonChartTooltip";
import { GenericGraph } from "app/pages/website-analysis/TrafficAndEngagement/Components/GenericGraph";
import { PopSingleModeLegends } from "app/pages/website-analysis/TrafficAndEngagement/Legends/PopSingleModeLegends";
import {
    tooltipPositioner,
    POPAndCompareTooltipPositioner,
} from "app/pages/website-analysis/TrafficAndEngagement/MD/chartConfig";
import {
    parseData,
    parsePOPCompareModeDataWithMTD,
    parsePOPSingleModeData,
} from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/ParseServerData";
import {
    chartGranularityItemClickArea,
    dateFormatter,
    getChartType,
    getGetExcel,
    getrInitPOPItemsSingleModeWebSources,
    getWebSourceForApiPOP,
    isTimeGranularityDisabled,
    isTimeGranularityDisabledOnlyMonth,
    minVisitsAbbrFormatter,
    neverStacking,
    OVERVIEW_EXCEL_ENDPOINT,
    POP_COMPARE_EXCEL_END_POINT,
    POP_SINGLE_CHART_TYPE,
    visitsNumberAbbrFormatter,
    YAxisVisitsNumberAbbrFormatter,
} from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import { ChartIds } from "pro-features/components/Chart/src/components/annotations/Helpers/ChartIdsHelper";

const ENDPOINT_METRIC = "Visits";

export const Visits = (props) => {
    const {
        name,
        meta: { isSingleMode, isPOP, isNonTotalWebSource, isFromAlternativeSources, isCategoryActive },
        getChartTooltipHeaders,
    } = props;

    const hdBannerCtaState = useHDBannerCta({ trackingName: "Graph tooltip/traffic over time" });
    /*
     * The add-on availability arrives after the chart config is built, and the tooltip formatter lives
     * on in that config, so it has to read the state when the tooltip opens rather than close over it
     */
    const hdBannerCtaStateRef = useRef(hdBannerCtaState);
    hdBannerCtaStateRef.current = hdBannerCtaState;
    const getHdBannerCtaState = useCallback(() => hdBannerCtaStateRef.current, []);

    let metricData;
    if (!isPOP) {
        const addToDashboardName = "EngagementVisits";
        const chartTooltip = (props) => (
            <CompareModeTooltip
                {...props}
                tableHeaders={getChartTooltipHeaders(props.timeGranularity)}
                xFormatter={dateFormatter}
                yFormatter={isFromAlternativeSources ? visitsNumberAbbrFormatter : minVisitsAbbrFormatter}
                totalAggregate={isCategoryActive ? null : (...values) => values.reduce((acc, v) => acc + v, 0)}
            />
        );
        const getExcelLink = getGetExcel(OVERVIEW_EXCEL_ENDPOINT);
        const yAxisLabelsFormatter = ({ value }) =>
            isFromAlternativeSources ? YAxisVisitsNumberAbbrFormatter({ value }) : minVisitsAbbrFilter()(value);
        metricData = {
            isTimeGranularityDisabled,
            getChartType,
            yAxisLabelsFormatter,
            addToDashboardName,
            chartType: METADATA_TYPE,
            getExcelLink,
            chartTooltip,
            parseData,
            getGraphData: parseData,
            chartGranularityItemClick:
                !isCategoryActive && isSingleMode && !isNonTotalWebSource ? chartGranularityItemClickArea : undefined,
            tooltipPositioner,
        };
    } else if (isSingleMode) {
        const addToDashboardName = "EngagementVisits";
        const legends = (props) => (
            <PopSingleModeLegends
                {...props}
                customLegends={true}
            />
        );
        const chartTooltip = (props) => (
            <PopTooltipSingle
                {...props}
                pointsData={props.points}
                xFormatter={dateFormatter}
                yFormatter={minVisitsAbbrFormatter}
            />
        );
        const getExcelLink = getGetExcel(OVERVIEW_EXCEL_ENDPOINT);
        const getGraphData = parsePOPSingleModeData;
        const yAxisLabelsFormatter = minVisitsAbbrFormatter;
        const getChartType = () => "line";
        const chartType = POP_SINGLE_CHART_TYPE;
        const getWebSourceForApi = getWebSourceForApiPOP;
        const getSites = (props) => getrInitPOPItemsSingleModeWebSources(props);
        metricData = {
            isTimeGranularityDisabled: isTimeGranularityDisabledOnlyMonth,
            getChartType,
            yAxisLabelsFormatter,
            getExcelLink,
            getGraphData,
            getWebSourceForApi,
            hideLegendsData: true,
            getSites,
            addToDashboardName,
            chartTooltip,
            legends,
            chartType,
            tooltipPositioner,
        };
    } else {
        const yAxisLabelsFormatter = ({ value }) => percentageSignFilter()(value, 0);
        const chartTooltip = (props) => (
            <PopTooltip
                pointsData={props.points}
                xFormatter={dateFormatter}
                yFormatter={minVisitsAbbrFormatter}
            />
        );
        const getGraphData = parsePOPCompareModeDataWithMTD;
        const getExcelLink = getGetExcel(POP_COMPARE_EXCEL_END_POINT);
        const popApiResponseName = "Visits";
        metricData = {
            isTimeGranularityDisabled: isTimeGranularityDisabledOnlyMonth,
            getChartType,
            yAxisLabelsFormatter,
            getExcelLink,
            getGraphData,
            chartTooltip,
            popApiResponseName,
            isStacking: neverStacking,
            hideLegendsData: true,
            tooltipPositioner: POPAndCompareTooltipPositioner,
        };
    }
    metricData.endpointMetric = ENDPOINT_METRIC;
    metricData.name = name;
    metricData.getHdBannerCtaState = getHdBannerCtaState;
    return (
        <GenericGraph
            metric={metricData}
            {...props}
            chartIdForAnnotations={`${ChartIds["TrafficAndEngagementVisits"]}`}
        />
    );
};
